import type { TFunction } from "i18next";
import _ from "lodash";

import { dateFormat, dateTimeFormat } from "./dates";

type ContactInfoLike = { itype: string; value: Nullable<string> };
type ContactOrInfosType = ContactInfoLike[] | Nilable<{ infos: ContactInfoLike[] }>;

function isContact(contact: unknown): contact is { infos: ContactInfoLike[] } {
  return typeof contact === "object" && contact !== null && "infos" in contact;
}

export const getContactInfo = (infos: ContactOrInfosType, type: ContactInfoType) => {
  if (!infos) {
    return undefined;
  }

  if (isContact(infos)) {
    infos = infos.infos;
  }

  return _.find(infos, (el) => el.itype === type);
};

export const getContactInfoValue = (infos: ContactOrInfosType, type: ContactInfoType) => {
  const info = getContactInfo(infos, type);
  if (info) {
    return info.value ?? undefined;
  }

  return undefined;
};

type ListDisplayNameContact = {
  isCompany: boolean;
  companyName: Nullable<string>;
  lastname: Nullable<string>;
  firstname: Nullable<string>;
};

export const listDisplayName = (contact: ListDisplayNameContact) => {
  if (contact.isCompany) {
    return contact.companyName;
  }

  let name = "";
  if (contact.lastname) {
    name = contact.lastname;
  }

  if (contact.firstname) {
    if (contact.lastname) {
      name += ", ";
    }

    name += contact.firstname;
  }

  return name;
};

export const getPhone = (contact: ContactOrInfosType) => getContactInfoValue(contact, "PRIVATE_PHONE");
export const getWorkPhone = (contact: ContactOrInfosType) => getContactInfoValue(contact, "WORK_PHONE");
export const getMobilePhone = (contact: ContactOrInfosType) => getContactInfoValue(contact, "MOBILE_PHONE");
export const getAnyPhone = (contact: ContactOrInfosType) =>
  getPhone(contact) || getWorkPhone(contact) || getMobilePhone(contact);
export const getEmail = (contact: ContactOrInfosType) => getWorkEmail(contact) || getPrivateEmail(contact);
export const getPrivateEmail = (contact: ContactOrInfosType) => getContactInfoValue(contact, "PRIVATE_EMAIL");
export const getWorkEmail = (contact: ContactOrInfosType) => getContactInfoValue(contact, "EMAIL");

export const getAddfields = <T = Record<string, unknown>>(
  contact: Nilable<{ attrs?: Nilable<Record<string, unknown>> }>,
): T | undefined => (contact?.attrs as Addfields<T> | undefined)?.addfields;

export const actionType = (t: TFunction, action: string | undefined, changes: any) => {
  if (action === "replace" && _.isEmpty(changes)) {
    return t("contacts:show.histories.actions.delete");
  }

  if (action) {
    return t(`contacts:show.histories.actions.${action}`);
  }

  return "";
};

type ContactAddressLike = {
  address?: Nullable<string>;
  zip?: Nullable<string>;
  city?: Nullable<string>;
};

export const addressString = (addr: Nilable<ContactAddressLike>) => {
  if (!addr) return null;
  return _.trim(`${addr.zip || ""} ${addr.city || ""}`);
};

export const fullAddressString = (addr: Nilable<ContactAddressLike>) => {
  if (!addr) return null;
  return _.trim(`${addr.address}, ${addr.zip || ""} ${addr.city || ""}`);
};

type NextFollowupEventLike = {
  allDay: boolean;
  startTime: string;
};

export const nextFollowupString = (nextFollowup: Nilable<NextFollowupEventLike>) => {
  if (!nextFollowup) {
    return null;
  }

  return nextFollowup.allDay ? dateFormat(nextFollowup.startTime) : dateTimeFormat(nextFollowup.startTime);
};

export const ITYPES: ContactInfoType[] = [
  "PRIVATE_PHONE",
  "WORK_PHONE",
  "MOBILE_PHONE",
  "EMAIL",
  "PRIVATE_EMAIL",
  "WEBSITE",
  "FAX",
  "EMERGENCY_CONTACT",
  "OTHER",
];

export const ATYPES: ContactAddressType[] = ["PRIMARY", "DELIVERY", "BILLING", "PRIVATE", "WORK", "OTHER"];

export const BASE_FIELDS = [
  "isCompany",
  "salutation",
  "firstname",
  "lastname",
  "companyName",
  "birthdate",
  "statusId",
  "ownerId",
  "creatorId",
  "editorId",
];
export const ADDRESS_FIELDS = ["address", "zip", "city", "cityPart", "country"];

export const EMAIL_ITYPES: ContactInfoType[] = ["EMAIL", "PRIVATE_EMAIL", "INVOICE_EMAIL"];
export const PHONE_ITYPES: ContactInfoType[] = ["PRIVATE_PHONE", "WORK_PHONE", "MOBILE_PHONE"];
